import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ritualChecked, setRitualChecked] = useState(false);
  const [adminMode, setAdminMode] = useState(true); // Default to admin mode

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  const SUPER_ADMIN_EMAIL = (process.env.REACT_APP_SUPER_ADMIN_EMAIL || 'rexedhlund@gmail.com').trim().toLowerCase();

  // Admin privileges are restricted to the single configured super-admin email
  const isSuperAdmin = !!SUPER_ADMIN_EMAIL && (user?.email || '').trim().toLowerCase() === SUPER_ADMIN_EMAIL;

  // Toggle admin mode (only for the super-admin)
  const toggleAdminMode = () => {
    if (isSuperAdmin) {
      setAdminMode(!adminMode);
      localStorage.setItem('admin_mode', (!adminMode).toString());
    }
  };

  // Load admin mode from localStorage
  useEffect(() => {
    const savedAdminMode = localStorage.getItem('admin_mode');
    if (savedAdminMode !== null) {
      setAdminMode(savedAdminMode === 'true');
    }
  }, []);

  // Computed property to determine if user should see admin features
  const isAdminMode = isSuperAdmin && adminMode;

  // Load auth state from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      // Don't automatically redirect to dashboard
      // Let the route component handle navigation based on ritual status
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      const { access_token, user: newUser } = response.data;
      
      setToken(access_token);
      setUser(newUser);
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(newUser));
      
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('admin_mode');
    setRitualChecked(false);
  };

  const checkDailyRitualStatus = async () => {
    if (!token) return false;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/daily-ritual-status`, { headers });
      console.log('🔍 Ritual status response:', response.data);
      setRitualChecked(true);
      // Return true if ritual is NOT needed (i.e., completed), false if ritual IS needed
      return !response.data.needs_ritual;
    } catch (error) {
      console.error('Error checking ritual status:', error);
      
      // If it's an authentication error, don't block navigation
      if (error.response?.status === 401) {
        console.log('⚠️ Authentication error - allowing navigation');
        return true; // Allow navigation to prevent infinite loop
      }
      
      return false;
    }
  };

  // FIXED: Use correct endpoint for refreshing user data
  const refreshUser = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use /auth/me endpoint which returns fresh user data from database
      const response = await axios.get(`${API}/auth/me`, { headers });
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/users/profile`, profileData, { headers });
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile update failed' 
      };
    }
  };

  const updateUsername = async (newUsername) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/users/username`, { username: newUsername }, { headers });
      const { access_token, user: updatedUser } = response.data;
      setToken(access_token);
      setUser(updatedUser);
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Username update error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Username update failed'
      };
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    refreshUser,
    updateProfile,
    updateUsername,
    isAuthenticated: !!token,
    ritualChecked,
    checkDailyRitualStatus,
    adminMode,
    isAdminMode,
    isSuperAdmin,
    toggleAdminMode,
    canToggleAdmin: isSuperAdmin
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-emerald-700">Loading your consciousness journey...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};